import ConversationAPI from '@/api/conversations/index.ts';
import { useI18n } from '@/hooks/useI18n.ts';
import { useNotify } from '@/hooks/useNotify.ts';
import { useWait } from '@/hooks/useWait.ts';
import type { MessageAttachment } from '@/entities/types/ConversationType.ts';

const MIN_DOWNLOAD_SPINNER_DURATION_MS = 600;
export const MAX_BLOB_URL_CACHE_SIZE = 100;

const blobUrlCache = new Map<number, string>();
const blobUrlPending = new Map<number, Promise<string>>();
const lastFreshUrlCache = new Map<number, string>();
let cacheGeneration = 0;

const conversationApi = new ConversationAPI();

export const clearBlobUrlCache = (): void => {
    for (const blobUrl of blobUrlCache.values()) {
        URL.revokeObjectURL(blobUrl);
    }
    blobUrlCache.clear();
    blobUrlPending.clear();
    lastFreshUrlCache.clear();
    cacheGeneration++;
};

const fetchBlob = async (url: string): Promise<Blob> => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
};

const fetchFreshBlobViaRefresh = async (attachment: MessageAttachment): Promise<Blob> => {
    const { url } = await conversationApi.fetchMediaTemporaryUrl(attachment.id);
    lastFreshUrlCache.set(attachment.id, url);
    return fetchBlob(url);
};

const fetchWithInitialUrlFallback = async (attachment: MessageAttachment): Promise<Blob> => {
    const initialUrl = lastFreshUrlCache.get(attachment.id) ?? attachment.url;
    const response = await fetch(initialUrl);

    if (response.ok) {
        return response.blob();
    }

    if (response.status === 403) {
        return fetchFreshBlobViaRefresh(attachment);
    }

    throw new Error(`HTTP error! status: ${response.status}`);
};

export const useAttachmentDownload = () => {
    const { is, start, end } = useWait();
    const { notify } = useNotify();
    const { t } = useI18n();

    const waitForMinimumSpinnerDuration = async (startedAtMs: number): Promise<void> => {
        const elapsedMs = Date.now() - startedAtMs;
        const remainingMs = Math.max(0, MIN_DOWNLOAD_SPINNER_DURATION_MS - elapsedMs);

        if (remainingMs > 0) {
            await new Promise((resolve) => {
                setTimeout(resolve, remainingMs);
            });
        }
    };

    const getWaitKey = (attachment: MessageAttachment): string =>
        `downloading-attachment-${attachment.id}`;

    const isDownloading = (attachment: MessageAttachment): boolean => is(getWaitKey(attachment));

    const fetchAttachmentBlobUrl = async (attachment: MessageAttachment): Promise<string> => {
        const cacheKey = attachment.id;

        const cached = blobUrlCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const pending = blobUrlPending.get(cacheKey);
        if (pending) {
            return pending;
        }

        const generation = cacheGeneration;
        const promise = fetchWithInitialUrlFallback(attachment)
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);

                if (generation !== cacheGeneration) {
                    URL.revokeObjectURL(blobUrl);
                    blobUrlPending.delete(cacheKey);
                    throw new Error('Cache invalidated');
                }

                if (blobUrlCache.size >= MAX_BLOB_URL_CACHE_SIZE) {
                    const oldestKey = blobUrlCache.keys().next().value!;
                    URL.revokeObjectURL(blobUrlCache.get(oldestKey)!);
                    blobUrlCache.delete(oldestKey);
                    lastFreshUrlCache.delete(oldestKey);
                }

                blobUrlCache.set(cacheKey, blobUrl);
                blobUrlPending.delete(cacheKey);

                return blobUrl;
            })
            .catch((error: unknown) => {
                blobUrlPending.delete(cacheKey);
                throw error;
            });

        blobUrlPending.set(cacheKey, promise);

        return promise;
    };

    const downloadAttachment = async (attachment: MessageAttachment): Promise<void> => {
        const waitKey = getWaitKey(attachment);

        if (is(waitKey)) {
            return;
        }

        const startedAtMs = Date.now();

        try {
            start(waitKey);

            const cachedBlobUrl = blobUrlCache.get(attachment.id);
            const blob = cachedBlobUrl
                ? await fetchBlob(cachedBlobUrl)
                : await fetchWithInitialUrlFallback(attachment);
            const downloadUrl = URL.createObjectURL(blob);

            try {
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = attachment.filename;

                document.body.appendChild(link);
                link.click();
                link.remove();
            } finally {
                URL.revokeObjectURL(downloadUrl);
            }
        } catch {
            notify.error(t('smsCommunication.messagesList.failedToDownloadAttachment'));
        } finally {
            await waitForMinimumSpinnerDuration(startedAtMs);
            end(waitKey);
        }
    };

    const openAttachmentInNewTab = async (attachment: MessageAttachment): Promise<void> => {
        const newTab = window.open('', '_blank');

        try {
            const blobUrl = await fetchAttachmentBlobUrl(attachment);
            if (newTab) {
                newTab.location.href = blobUrl;
            }
        } catch {
            newTab?.close();
            notify.error(t('smsCommunication.messagesList.failedToDownloadAttachment'));
        }
    };

    return {
        downloadAttachment,
        fetchAttachmentBlobUrl,
        isDownloading,
        openAttachmentInNewTab,
    };
};
